/**
 * Hooks del panel de accesos (`/configuracion/accesos`, solo platform_admin).
 *
 * Todas las queries llevan `enabled: isPlatformAdmin` para no lanzar peticiones
 * que acabarían en 403 — mismo criterio que `useLLMUsageStats`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";
import type { components } from "@/lib/api/generated";

export type AccessSummary = components["schemas"]["AccessSummary"];
export type AdminUserRow = components["schemas"]["AdminUserRow"];
export type AccessLogItem = components["schemas"]["AccessLogItem"];
export type AccessLogPage = components["schemas"]["AccessLogPage"];
export type LoginAttempt = components["schemas"]["LoginAttempt"];
export type SecurityOverview = components["schemas"]["SecurityOverview"];

const ACCESS_KEY = ["access"] as const;

/** Filtros del registro de accesos (se sincronizan con la URL). */
export interface AccessLogFilters {
  page?: number;
  page_size?: number;
  user_id?: string;
  action?: string;
  category?: string;
  outcome?: string;
  entity_type?: string;
  entity_id?: string;
  ip?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
}

function toQueryString(filters: AccessLogFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useAccessSummary(enabled = true) {
  return useQuery<AccessSummary, Error>({
    queryKey: [...ACCESS_KEY, "summary"],
    queryFn: () => fetchWithAuth("/api/v1/admin/summary"),
    enabled,
    staleTime: 30_000,
    // "Conectados ahora" necesita refrescarse solo; el resto no lo justifica
    // con 11 usuarios.
    refetchInterval: enabled ? 60_000 : false,
  });
}

export function useAccessUsers(enabled = true) {
  return useQuery<AdminUserRow[], Error>({
    queryKey: [...ACCESS_KEY, "users"],
    queryFn: () => fetchWithAuth("/api/v1/admin/users"),
    enabled,
    staleTime: 30_000,
  });
}

export function useAccessLogs(filters: AccessLogFilters, enabled = true) {
  return useQuery<AccessLogPage, Error>({
    queryKey: [...ACCESS_KEY, "logs", filters],
    queryFn: () => fetchWithAuth(`/api/v1/admin/access-logs${toQueryString(filters)}`),
    enabled,
    // Auditoría: siempre fresco, sin caché.
    staleTime: 0,
  });
}

export function useSecurityOverview(days = 30, enabled = true) {
  return useQuery<SecurityOverview, Error>({
    queryKey: [...ACCESS_KEY, "security", days],
    queryFn: () => fetchWithAuth(`/api/v1/admin/security?days=${days}`),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();

  return useMutation<AdminUserRow, Error, { userId: string; isActive: boolean }>({
    mutationFn: ({ userId, isActive }) =>
      fetchWithAuth(`/api/v1/admin/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: isActive }),
      }),
    // Invalidación, no actualización optimista: con 11 filas y ~80 ms de
    // latencia, el rollback optimista es complejidad sin beneficio.
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ACCESS_KEY });
      showToast.success(
        user.is_active
          ? `${user.email} puede volver a acceder`
          : `${user.email} ya no tiene acceso`
      );
    },
    onError: (error) => {
      showToast.error(error.message || "No se pudo cambiar el estado del usuario");
    },
  });
}

/** URL del export CSV (se abre directamente; el backend lo audita). */
export function accessLogsCsvUrl(days = 30): string {
  return `/api/v1/admin/access-logs/export.csv?days=${days}`;
}
